// @ts-strict-ignore
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { LMFieldServiceAdapter } from '@field/lm-field-service-adapter';
import { LMModel, LMModelChangedEvent, LMModelField, LMModelStatusEvent } from '@form/model';
import { LMFieldBaseComponent, LMInvalidLPLEvent, LMPreviewEvent } from '@field/lm-field-base.component';
import { LMFilterBuilderService } from '@modal/filter-builder';
import { BehaviorSubject, firstValueFrom, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, flatMap, takeUntil, takeWhile } from 'rxjs/operators';
import { AppConstants, FilterBuilderDialogResult, LMDialogButtonKey } from '@utils/app.constants';
import { LMCubeCellRelService } from '@modal/cube-cell-rel/services';
import { LMCubeCellRelationDefinition } from '@modal/cube-cell-rel/lm-cube-cell-rel';
import { LMComputeBuilderService } from '@modal/compute-builder/services';
import { LMDimension, LMMeasure } from '@modal/cubes/lm-cubes';
import { LMLplEditorService } from '@lplEditor/lm-lpl-editor.service';
import {
  LMBusclassFieldService,
  LMFieldPickerService,
  LMFormRequestService,
  LMSelectDialogService,
  LMSelectResponse
} from '@app/components';
import {LMCloseOverlayInfo, LMErrorUtilService, LMOverlayInfo, LMOverlayService, LMSpecService} from "@app/services";
import IWordAtPosition = monaco.editor.IWordAtPosition;
import urltemplate from "url-template";
import { KeyedResource } from "@app/resources";
import { SohoMessageService } from "ids-enterprise-ng";
import {
  LMFieldPickerOperatorOptions, LMFieldPickerSelectOptions,
  LMFieldPickerSelectResponse,
} from "@modal/field-picker/lm-field-picker";

@Component({
  selector: 'lm-busclassfield',
  standalone: false,
  templateUrl: './lm-busclassfield.component.html',
  styleUrls: [ './lm-busclassfield.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  })
export class LMBusclassFieldComponent extends LMFieldBaseComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('editorContainer') _editorContainer: ElementRef;

  private unsubscribe$ = new Subject<void>();

  @Input() layoutObject: LMBussClassFieldLayout;
  @Input() specFields: LMSpecFields;
  @Input() model: LMModel;

  @HostBinding('attr.data-automation-id') bcfAutomationId = '';

  public computeOptionAutomationId = '';
  public derivedOptionAutomationId = '';
  public previewButtonAutomationId = '';
  public builderButtonAutomationId = '';
  public addMetricButtonAutomationId = '';
  public addRatioButtonAutomationId = '';
  public addVarianceAmountButtonAutomationId = '';
  public addVariancePercentButtonAutomationId = '';
  public AddMetricMenuButtonAutomationId = '';

  // private tooltipElement: HTMLElement | null = null;
  // private mouseMoveListener: ((e: MouseEvent) => void) | null = null;
  // private mouseLeaveListener: (() => void) | null = null;

  rows: number = undefined;

  // Cube Cell Relation
  private dimensionMapping: Array<LMDimension>;
  private measures: Array<LMMeasure>;

  computeType: string;
  customDerivedLpl: string;
  customComputeLpl: string;

  queryValidateEventDebouncer$ = new Subject<string>();
  invalidLPLEventDebouncer$ = new Subject<string>();

  // start out disabled until the user places a cursor on a metric.
  viewMetricButtonDisabled = true;

  public monacoOptions: monaco.editor.IStandaloneEditorConstructionOptions = {
    theme:           'vs',
    automaticLayout: true,
    language:        'LPL',
    foldingStrategy: 'indentation',
    contextmenu: false,
    minimap: {
      enabled: false
    },
    lineDecorationsWidth: 0,
    lineNumbersMinChars: 2,
    quickSuggestions: false,
    wordWrap: 'on',
    scrollbar: {
      horizontal: 'hidden'
    },
    overviewRulerLanes: 0,
    hideCursorInOverviewRuler: true,
    overviewRulerBorder: false,
    scrollBeyondLastLine: false
  };


  // static template variables
  public isBusClassTotal = false;
  public isBusClassGroup = false;
  public isBusClassCompute = false;
  public isBusinessMetricCompute = false;
  public isBusinessMetricTotal = false;
  public hideTestMeasures = false;

  public disableDimensions$ = new BehaviorSubject(false);

  private editor: monaco.editor.IStandaloneCodeEditor;
  private busClassModelField: LMModelField;
  private monacoHoverProvider: monaco.IDisposable | undefined = undefined;
  private monacoCompletionProvider: monaco.IDisposable | undefined = undefined;
  private monacoDotTriggerCompletionProvider: monaco.IDisposable | undefined = undefined;
  private monacoLinkProvider: monaco.IDisposable | undefined = undefined;
  private cursorPositionDisposable: monaco.IDisposable | undefined = undefined;
  private okBtnClickHandler: () => void;
  private promptUserAction: monaco.IDisposable;
  private metricDataView: LMDataView;

  private static MENUITEM_ADD_METRIC = 'AddMetric';
  private static MENUITEM_ADD_RATIO = 'AddRadio';
  private static MENUITEM_ADD_VARIANCE_AMOUNT = 'AddVarianceAmount';
  private static MENUITEM_ADD_VARIANCE_PERCENT = 'AddVariancePercent';
  private static MENUITEM_ADD_PERIOD_OPERATOR = 'AddPeriodOperator';
  private static MENUITEM_ADD_MEASURE = 'AddMeasure';

  public isDisabled = false;
  // private static MENUITEM_ADD_PERIOD_OPERATOR = 'AddPeriodOperator';
  // private static MENUITEM_VIEW_METRIC = 'ViewMetric';
  // private static MENUITEM_COPY_METRIC = 'CopyMetric';


  constructor(
    private sohoMessageService: SohoMessageService,
    private lmErrorUtilService: LMErrorUtilService,
    protected lmFieldServiceAdapter: LMFieldServiceAdapter,
    private lmFormRequestService: LMFormRequestService,
    private lmFilterBuilderService: LMFilterBuilderService,
    private lmCubeCellRelService: LMCubeCellRelService,
    private lmComputeBuilderService: LMComputeBuilderService,
    private lmSelectDialogService: LMSelectDialogService,
    private lmSpecService: LMSpecService,
    private changeDetectionRef: ChangeDetectorRef,
    private lmLplEditorService: LMLplEditorService,
    private lmBusclassFieldService: LMBusclassFieldService,
    private lmOverlayService: LMOverlayService,
    private lmFieldPickerService: LMFieldPickerService
  ) {
    super(lmFieldServiceAdapter);
  }

  // events
  ngOnInit() {
    super.ngOnInit();
    this.initialize(this.layoutObject, this.specFields, this.model);

    this.setVariables();

    if (this.isBusClassTotal) {
      const busClassName = this.layoutObject?.lplFieldInfo?.busClassName ? this.layoutObject.lplFieldInfo.busClassName : 'BusinessClass';

      this.busClassModelField = this.model.getField(busClassName);

      // need to listen to value changes of the Business Class field, so we can correctly enable the Set Dimensions button
      this.subscriptionArray.push(this.busClassModelField.valueChanged.subscribe(() => {
        this.disableDimensions$.next(this.calculateDisableDimensions());
      }));
    }

    this.bcfAutomationId = `lm-${this.fieldAutomationId}-field`;
    this.computeOptionAutomationId = `lm-${this.fieldAutomationId}-compute-field-option`;
    this.derivedOptionAutomationId = `lm-${this.fieldAutomationId}-derived-field-option`;
    this.previewButtonAutomationId = `lm-${this.fieldAutomationId}-preview-button`;
    this.builderButtonAutomationId = `lm-${this.fieldAutomationId}-builder-button`;
    this.addMetricButtonAutomationId  = `lm-${this.fieldAutomationId}-add-metric-button`;
    this.addRatioButtonAutomationId = `lm-${this.fieldAutomationId}-add-ratio-button`;
    this.addVarianceAmountButtonAutomationId = `lm-${this.fieldAutomationId}-add-variance-amount-button`;
    this.addVariancePercentButtonAutomationId = `lm-${this.fieldAutomationId}-add-variance-percent-button`;
    this.AddMetricMenuButtonAutomationId = `lm-${this.fieldAutomationId}-add-menu-button`;

    if (this.isBusClassCompute) {
      this.determineTypeAndLPL();
    }

    this.rows = (this.layoutObject.lines === undefined || this.layoutObject.lines === 0) ? 4 : this.layoutObject.lines;

    if (this.isBusClassTotal && this.rows < 10) {
      this.rows = 10;
    }
    if (this.specField.isLpl) {
      this._fieldClassArray.push('lm-monospace');
    }

    if (this.displayAsText) {
      this._fieldClassArray.push('lm-text-data hide-white-space');
    } else {
      this.subscribeToInlineError();
    }
    this.setFieldClasses();

    this.queryValidateEventDebouncer$
      .pipe(debounceTime(1250))
      .subscribe((businessClass) => {
        this.validateLplQuery(businessClass);
      });

    // This a workaround because the 'Add Metric' button needs to be
    // disabled if no metric is highlighted in the editor. But also needs
    // to happen when the field is set to display only in the lpl.
    this.subscriptionArray.push(this.readOnly$.subscribe((disable: boolean) => {
      this.isDisabled = disable;
    }));

    this.invalidLPLEventDebouncer$
      .pipe()
      .subscribe((hasLPLValidationError) => {
        this.fieldEvent?.emit(new LMInvalidLPLEvent(event, JSON.parse(hasLPLValidationError)));
      });

    this.subscriptionArray.push(this.model.modelChanged
      .subscribe((modelChangedEvent: LMModelChangedEvent) => {
        if (modelChangedEvent.eventType === 'set') {
          this.handleModelChanged();

          this.hideTestMeasures = this.isReadOnly || this.model.isDirty || this.model.isCreateAction || (this.modelField.value as string).length <= 0;
        }
      })
    );

    this.subscriptionArray.push(this.model.statusChanges
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
      .subscribe((modelChangedEvent: LMModelStatusEvent) => {
        // since the lm-lpl-editor doesn't extend LMFieldBase, we need to recalculate read only for the editor here.
        this.updateReadOnlyState();
      })
    );

    this.subscriptionArray.push(this.modelField.valueChanged
      .subscribe(() => {
        this.hideTestMeasures = this.isReadOnly || this.model.isDirty || this.model.isCreateAction || (this.modelField.value as string).length <= 0;
      })
    );
  }

  private handleModelChanged() {
    // need to set input on the component for the change detection to work!
    if (this.editor) {
      const model = this.editor.getModel() as monaco.editor.ITextModel;
      model.setValue(this.modelField.value ? this.modelField.value : '');
    }
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
    // Set the soho alert id so the lpl error messages with quotes and caret display correctly.
    // TODO: Not sure if condition error can use it or not.
    if (this.isBusClassTotal) {
      if (this.sohoAlert) {
        this.sohoAlert.id = this.id;
      }
    }
    this.setVariables();
    this.initMonaco();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.queryValidateEventDebouncer$.next('');
    this.queryValidateEventDebouncer$.complete();
    this.disableDimensions$.next(false);
    this.disableDimensions$.complete();

    if (this.isBusinessMetricCompute) {
      this.monacoHoverProvider?.dispose();
      this.monacoCompletionProvider?.dispose();
      this.monacoDotTriggerCompletionProvider?.dispose();
      this.monacoLinkProvider?.dispose();
      this.promptUserAction?.dispose();
      this.cursorPositionDisposable?.dispose();
    }

    // if (this.isBusinessMetricCompute) {
    //   if (this.editor) {
    //     const domNode = this.editor.getDomNode();
    //     if (domNode && this.mouseMoveListener) {
    //       domNode.removeEventListener('mousemove', this.mouseMoveListener);
    //     }
    //     if (domNode && this.mouseLeaveListener) {
    //       domNode.removeEventListener('mouseleave', this.mouseLeaveListener);
    //     }
    //   }
    //   this.hideTooltip();
    // }

    if (this.editor) {
      this.editor.dispose();
    }
  }

  public contextMenuClicked = ($event) => {
    this.onContextMenuEvent($event);
  };

  public onBuildButtonEvent() {
    const businessClass = this.getBusinessClass();
    if (this.validateBusinessClass(businessClass)) {
      this.openBuilder(businessClass);
    }
  }

  private openBuilder(businessClass: string) {
    if (this.layoutObject.lplFieldInfo?.mode === 'DerivedField') {
      const builderResult$ = this.lmComputeBuilderService.openDerivedComputeBuilderDialog(businessClass,
        this.layoutObject.lplValidationUrl,
        this.model.formResourceId,
        this.modelField.value);

      builderResult$.pipe(
        takeUntil(this.unsubscribe$),
        distinctUntilChanged()
      ).subscribe((result: LMComputeBuilderResult) => {

        if (result.action === 'Ok') {
          this.modelField.value = result.lplQuery;
          this.model.setDirty(this.layoutObject.bindId, true);
          this.handleModelChanged();
        }
      });
    } else {
      const options: LMFilterBuilderOptions = {
        businessClass: businessClass,
        fieldLookUpUrl: this.layoutObject.fieldLookUpUrl,
        fieldSearchUrl: this.layoutObject.fieldSearchUrl,
        lplValidationUrl: this.layoutObject.lplValidationUrl,
        hasExistingCondition: this.modelField.value.trim().length > 0 ? true : false,
        fieldPickerView: this.lmFieldPickerService.createFieldPickerView(this.layoutObject.lplFieldInfo, 'InstanceSelection'),
        includeAuditFields: false,
      };

      // override, should be more explicit
      if (this.isBusinessMetricCompute) {
        options.validationType = 'metric-compute';
      } else if (this.isBusinessMetricTotal) {
        options.validationType = 'metric-total';
      }

      this.lmFilterBuilderService.openBuilderDialog(options).pipe(
        takeUntil(this.unsubscribe$),
        takeWhile((result: LMFilterBuilderResult) => (result.lplQuery && result.lplQuery !== this.modelField.value)),
        flatMap(result => {
          const lplFieldPicker  = result.lplQuery;

          if (result.dialogResult === FilterBuilderDialogResult.CONFIRMATION_OK && !this.modelField.value) {
            return of(lplFieldPicker);
          }

          if (result.dialogResult === FilterBuilderDialogResult.CONFIRMATION_APPEND) {
            return of(`${this.modelField.value} ${result.matchOption} ${result.lplQuery}`);
          }

            // dialogResult is null if there's no this.modelField.value
          if (!result.dialogResult || result.dialogResult === FilterBuilderDialogResult.CONFIRMATION_OVERWRITE) {
            return of(result.lplQuery);
          }

        })).subscribe((value: string) => {
        this.modelField.value = value;
        this.handleModelChanged();
        this.model.setDirty(this.layoutObject.bindId, true);
        this.changeDetectionRef.markForCheck();
      });
    }
  }

  onPreviewButtonEvent() {
    const businessClass = this.getBusinessClass();
    if ((this.modelField.value as string)?.trim()?.length > 0) {
      if (this.validateBusinessClass(businessClass)) {
        this.showPreview(businessClass);
      }
    } else {
      this.sohoAlert.addInlineMessage(Soho.Locale.translate('BusClassFieldRequired'), undefined, undefined, undefined, undefined, 'error-bcf-message');
    }
  }

  private showPreview(businessClass: string) {
    this.fieldEvent.emit(new LMPreviewEvent(event, this.modelField.value, businessClass, this.layoutObject.bindId));
  }

  onBuildCubeRelation() {
    const businessClass = this.getBusinessClass();
    if (this.validateBusinessClass(businessClass)) {
      this.runBuildCubeRelation(businessClass);
    }
  }

  private runBuildCubeRelation(businessClass) {
    const busClassTotalName: string = <string> this.model.getData().fields['BusinessClassTotal'].value;
    const definition: string = this.modelField.value;
    const dimensionMapping: Array<LMDimension> = (this.model.isDirty) ? undefined : this.dimensionMapping;
    // Open the dialog if the business class is not empty
    this.lmCubeCellRelService.buildCubeCellRelDimensionsDialog(this.placeHolder, busClassTotalName, businessClass, definition, dimensionMapping)
      .subscribe((response: LMCubeCellRelationDefinition) => {
        if (response && response.relationLpl && response.relationLpl.length > 0) {
          this.modelField.value = response.relationLpl;
          this.model.setDirty(this.layoutObject.bindId, true);
          this.handleModelChanged();
        }
        if (response && response.dimensionMapping && !this.model.isCreateAction) {
          this.dimensionMapping = response.dimensionMapping;
        }
      });
  }

  onAfterResetDirty(event: SohoTrackDirtyEvent) {
    if (this.isBusClassCompute) {
      this.customDerivedLpl = '';
      this.customComputeLpl = '';

      this.determineTypeAndLPL();
    }

    this.changeDetectionRef.detectChanges();

    super.onAfterResetDirty(event);
  }

  onPreviewCubeRelation() {
    // Run the test only if the form is not dirty.
    if (this.model.isDirty || this.model.isCreateAction) {
      return;
    }
    const businessClass = this.getBusinessClass();
    if (this.validateBusinessClass(businessClass)) {
      this.runPreviewCubeRelation(businessClass);
    }
  }

  private runPreviewCubeRelation(businessClass: string) {
    const busClassTotalName: string = <string> this.model.getData().fields['BusinessClassTotal'].value;
    this.lmCubeCellRelService.buildCubeCellRelMeasuresTestDialog(this.placeHolder, busClassTotalName, businessClass, this.measures)
      .subscribe((measures: Array<LMMeasure>) => {
        // Save it off so that we don't have to go back to fetch.
        this.measures = measures;
      });
  }

  private determineTypeAndLPL() {
    this.computeType = this.model.getData().fields[this.bindId].customComputeType;

    if (this.computeType === 'derived') {
      this.customDerivedLpl = this.modelField.value;
    } else {
      this.customComputeLpl = this.modelField.value;
    }

    if (!this.computeType) {
      this.computeType = 'compute';
    }
  }

  handleComputeTypeChange(event) {

    // change is getting triggered twice, once natively and once from the soho-radio button component. We
    // only want to do things when the change is coming from soho-radio button, so make sure there is
    // an originalEvent on the event
    if (event.originalEvent) { // Added checked handler, change event being triggered twice.
      // Store model field value
      if (this.computeType === 'compute') {
        this.customDerivedLpl = this.modelField.value;
      } else {
        this.customComputeLpl = this.modelField.value;
      }

      // Set new value based on the computeType
      if (this.computeType === 'derived') {
        this.modelField.value = this.customDerivedLpl;
      } else {
        this.modelField.value = this.customComputeLpl;
      }

      if (this.modelField.value) {
        this.model.setDirty(this.layoutObject.bindId, true);
      }

      // always validate the lpl, since this will clear an error
      // when switching between compute and derived, but only
      // do the call to back end if a there is a modelField.value
      this.validateLplQuery(this.getBusinessClass());

      this.handleModelChanged();
    }
  }

  onComputeBuildButtonEvent() {
    const businessClass = this.getBusinessClass();
    if (this.validateBusinessClass(businessClass)) {
      this.runComputeBuilder(businessClass);
    }
  }

  onAddToMetricCompute(type: LMAddToMetricComputeType, disallowMeasureOverride: boolean): void {
    const metricSelectListUrl = this.buildBusMetricSelectUrl();

    const measurePickerOptions: LMFieldPickerSelectOptions = {
      view: 'Measures',
      businessClass: this.getBusinessClass(),
    };

    const measureSelectListUrl = this.lmFieldServiceAdapter.lmUrlUtilService.buildFieldLookupUrl(this.model.formUrlInfo);

    this.lmBusclassFieldService.openAddToMetricDialog(
        // this.model.formUrlInfo,
        type,
        metricSelectListUrl,
        this.model.getData(),
        measureSelectListUrl,
        measurePickerOptions,
        disallowMeasureOverride
    ).subscribe((result: LMAddToMetricComputeResult) => {
      if (result.action === 'Ok') {
        console.log('LMAddToMetricComputeResult: ', result);

        const appendText = this.lmBusclassFieldService.buildAddToMetricString(type, result);

        if (this.modelField?.value.trim().length === 0) {
          this.modelField.value = appendText;
        } else {
          // if appending a calculation to a current value surround the calc
          // with parens.
          this.modelField.value = this.modelField.value + ' + (' + appendText + ')';
        }

        this.model.setDirty(this.layoutObject.bindId, true);
        this.handleModelChanged();
      }
    });
  }

  private buildBusMetricSelectUrl() {
    const selectListInfo: LMSelectListInfo = {
      selectListResourceId: 'BusinessMetric.BusinessMetrics',
      selectResourceId: 'BusinessMetric.BusinessMetric',
      selectResourceType: 'select'
    };
    return this.lmFieldServiceAdapter.lmUrlUtilService.buildSelectListUrl(this.model.formUrlInfo, selectListInfo);
  }

  onAddMetricEvent() {
    const selectListUrl = this.buildBusMetricSelectUrl();
    this.lmSpecService.getListSpec(selectListUrl).subscribe((listSpecResponse: LMListSpecResponse) => {
      this.lmFieldServiceAdapter.lmLoggerService.log(['LMLookupComponent', 'getListSpec() completed', listSpecResponse]);

      const listUrlInfo = listSpecResponse.listUrlInfo;
      const listSpec = listSpecResponse.listSpec;

      // If the list spec has search form, it is either displayed initially or the user will click on search icon to open up.
      // In either case, do not set the height of the modal. The search form and the list together should be in auto height.
      // Also in the case of tree view, do not want to set the modal with overflow:hidden. As the nodes are expanded, we should be able to scroll.
      const className = (listSpec.busListSpec.searchForm === undefined && !listSpec.busListSpec.implementsTreeView) ? 'lm-lookup-modal' : '';

      // const labelValue = this.labelResourceValue ? this.lmFieldServiceAdapter.lmFormatUtilService.formatMessageRule(this.labelResourceValue, this.model, this.specFields) : '';
      // const title = (labelValue !== '')
      //   ? this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('SelectTitle'), "Business Metric")
      //   : Soho.Locale.translate('Select');
      const title = this.lmFieldServiceAdapter.lmFormatUtilService.formatMessage(Soho.Locale.translate('SelectTitle'), 'Business Metric');
      const editTitle = Soho.Locale.translate('Edit');

      const formSpec = this.model.formSpec;
      const selectRequestData: SelectRequestData = {
        viewType: formSpec.selectViewType ? formSpec.selectViewType : 'form',
        viewName: this.lmFieldServiceAdapter.lmFormSpecUtilService.formName(formSpec),
        dataView: this.getSearchFormValidatedDataView(),
        allowMultiSelections: true,
        maintainSelectedValues: true
      };

      const selectedValuesData: SelectedValuesData = {
        selectedValues: '',
        fieldId: 'BusinessMetric',
        type: 'ALPHALOWER',
        decSize: undefined,
        precision: undefined,
        selectTitle: title,
        editTitle: editTitle
      };
      selectRequestData.selectedValuesData = selectedValuesData;

      if (this.model.action) {
        selectRequestData.actionName = this.model.action;
      }

      this.lmSelectDialogService.showSelectListDialog({
        listUrlInfo,
        listSpec,
        title,
        sourceField: 'BusinessMetric',
        className,
        selectRequestData
      }).pipe(
        first()) // causes the subscription to complete after its messaged the first time
        .subscribe((selectResponse: LMSelectResponse) => {
            this.lmFieldServiceAdapter.lmLoggerService.log([ 'LMLookupComponent', 'showSelectListDialog()', selectResponse ]);
            if (selectResponse?.status === AppConstants.STATUS_COMPLETED && (selectResponse.dataViews || selectResponse.multiSelectedValues)) {
              // clear any errors
              // this.model.clearFieldError(this.targetField);

              let appendText = '';
              if (selectResponse.multiSelectedValues?.length > 0) {
                appendText = selectResponse.multiSelectedValues.reduce((acc, multiSelectedValue, currentIndex) => {
                  if (currentIndex > 0) {
                    acc += ' + ';
                  }
                  return acc + '[' + multiSelectedValue.formattedValue + ']';
                }, '');

              } else {
                appendText = selectResponse.dataViews.reduce((acc, dataView, currentIndex) => {
                  if (currentIndex > 0) {
                    acc += ' + ';
                  }
                  return acc + '[' + (dataView as any).fields['BusinessMetric'].value + ']';
                }, '');
              }

              // const model = this.editor.getModel() as monaco.editor.ITextModel;
              // const currentValue = model.getValue();
              // if (currentValue?.trim().length === 0) {
              //   model.setValue(appendText);
              // } else {
              //   model.setValue(currentValue + ' + ' + appendText);
              // }

              if (this.modelField?.value.trim().length === 0) {
                this.modelField.value = appendText;
              } else {
                this.modelField.value = this.modelField.value + ' + ' + appendText;
              }

              this.model.setDirty(this.layoutObject.bindId, true);
              this.handleModelChanged();

            } else if (selectResponse?.status === AppConstants.STATUS_CANCEL) {
              this.getInputElement()?.focus();
            }
            this.busyIndicator = false;
          }
        );
    });
  }

  private onAddMeasure(model, position): void {
    const options: LMFieldPickerSelectOptions = {
      view: 'Measures',
      businessClass: this.getBusinessClass(),
    };

    const lookUpUrl = this.lmFieldServiceAdapter.lmUrlUtilService.buildFieldLookupUrl(this.model.formUrlInfo);
    this.lmFieldPickerService.openFieldPickerDialog(options, lookUpUrl, undefined)
      .subscribe({
        next: (response: LMFieldPickerSelectResponse) => {
          this.addMeasureToMetric(response, model, position);
          console.log('response', [ response ]);
        },
        error: (error: any) => {
          const error2: LMError = { message: error.message, status: error.status, details: error.stack };
          this.lmErrorUtilService.handleUnexpectedError(error2);
        }
      });

    // this.showCustomModal();
  }

  private addMeasureToMetric(response: LMFieldPickerSelectResponse, model, position): void {
    if (!response?.fieldName) {
      return;
    }

    // const word = this.editor.getModel().getWordAtPosition(position);
    const result = this.extractBracketContentsAtPosition(model, position);

    const metricInfo = new LMMetricInfo(result.content);
    metricInfo.setMeasure(response.fieldName);
    const newMetricText = metricInfo.toString();

    this.editor.executeEdits('replace', [
      {
        range: result.range,
        text: `[${newMetricText}]`
      }
    ]);

    console.log('addMeasureToMetric', [ newMetricText ]);
  }

  // private parseMetricText(result: any): any {
  //   const content = result.fullMatch;
  //
  //   const metric = new MetricInfo("KPI_Cash_CashEquivalents.NetAmount period beginning balance");
  //   metric.setMeasure("NewMeasure");
  //   console.log(metric.toString());
  //
  //   return this.parseMetricUsage(content);
  // }
  //
  // private parseMetricUsage(metricText: string): { metricName: string; measure?: string; periodOperators?: string } {
  //   const match = metricText.match(/^([^.\s]+)(?:\.([^\s]+))?(?:\s+(.+))?$/);
  //   if (!match) return { metricName: metricText };
  //
  //   return {
  //     metricName: match[1],
  //     measure: match[2],
  //     periodOperators: match[3]
  //   };
  // }
  //
  // private showCustomModal(): void {
  //   // Your modal logic here
  //   const buttons = [
  //     { text: 'OK', click: (e, modal) => { modal.close(true); } },
  //     { text: 'Cancel', click: (e, modal) => { modal.close(false); } }
  //   ];
  //
  //   this.lmModalWrapperService
  //     .message()
  //     .message('Dot was typed!')
  //     .title('Dot Trigger Modal')
  //     .buttons(buttons)
  //     .open();
  // }

  private runComputeBuilder(businessClassName) {
    let builderResult$: Observable<LMComputeBuilderResult>;
    if (this.computeType === 'compute') {
      builderResult$ = this.lmComputeBuilderService.openComputeBuilderDialog(businessClassName,
        this.layoutObject.fieldLookUpUrl,
        this.layoutObject.fieldSearchUrl,
        this.layoutObject.lplValidationUrl,
        false,
        this.isBusClassCompute,
        false,
        this.modelField.value);
    } else {
      builderResult$ = this.lmComputeBuilderService.openDerivedComputeBuilderDialog(businessClassName,
        this.layoutObject.lplValidationUrl,
        this.model.formResourceId,
        this.modelField.value);
    }

    builderResult$.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged()
    ).subscribe((result: LMComputeBuilderResult) => {

      if (result.action === 'Ok') {
        this.modelField.value = result.lplQuery;
        this.model.setDirty(this.layoutObject.bindId, true);
        this.handleModelChanged();
      }
    });
  }

  private runDerivedFieldBuilder(businessClassName) {
    const builderResult$ = this.lmComputeBuilderService.openDerivedComputeBuilderDialog(businessClassName,
      this.layoutObject.lplValidationUrl,
      this.model.formResourceId,
      this.modelField.value);

    builderResult$.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged()
    ).subscribe((result: LMComputeBuilderResult) => {

      if (result.action === 'Ok') {
        this.modelField.value = result.lplQuery;
        this.model.setDirty(this.layoutObject.bindId, true);
        this.handleModelChanged();
      }
    });
  }

  private initMonaco() {
    if (!this.lmLplEditorService.isMonacoAvailable()) {
      this.lmLplEditorService.loadingMonacoFinished.pipe(first()).subscribe(() => {
        this.initMonaco();
      });
      return;
    }

    if (this.isBusinessMetricCompute) {
      // turn off line numbers for a bus metric compute field.
      this.monacoOptions.lineNumbers = "off";

      //  do we want a single line editor, single line editor optinos?
      this.monacoOptions.wordWrap = "on";
      this.monacoOptions.scrollBeyondLastLine = false;
      this.monacoOptions.scrollbar = {
        // vertical: 'hidden',
        horizontal: 'hidden'
      };
      this.monacoOptions.overviewRulerLanes = 0;
      this.monacoOptions.hideCursorInOverviewRuler = true;
      this.monacoOptions.overviewRulerBorder = false;

      // trying to hide the gutter where the line numbers display.
      //  these options do not seem to work however.
      // this.monacoOptions.lineNumbersMinChars = 0;
      // this.monacoOptions.lineDecorationsWidth = 0;
      // this.monacoOptions.glyphMargin = false;

    }
    this.editor = monaco.editor.create(this._editorContainer.nativeElement, this.monacoOptions);

    if (this.isBusinessMetricCompute) {
      // Prevent new lines, needed if we want a single line editor.
      this.editor.addCommand(monaco.KeyCode.Enter, () => {
        // Do nothing - prevents new lines
      });
    }

    this.lmLplEditorService.syncMonacoThemeToSohoTheme();

    const model = this.editor.getModel() as monaco.editor.ITextModel;
    model.setValue(this.modelField.value);

    this.updateReadOnlyState();
    this.addMonacoListeners();
    // this.addCustomTooltipListener();
    this.addMonacoProviders();

    if (!this.model.isInOverlay) {
      this.subscriptionArray.push(this.lmOverlayService.overlayClose.subscribe((closeInfo: LMCloseOverlayInfo) => {
        if (closeInfo.canClose) {
          console.log('overlay has been closed');
        }
      }));
    }
  }

  private buildHoverValue(dataView: LMDataView): string {
    let hoverValue = '';
    // hoverValue += dataView.fields['DisplayName'].value + '\n\n';
    // hoverValue += '&nbsp;\n\n';
    hoverValue += dataView.fields['Description'].value + '\n\n';
    return hoverValue;
  }

  private wordIsAMetricName(model: monaco.editor.ITextModel, word: IWordAtPosition): boolean {
    const possibleMetric = word.word;
    const fieldValue: string = model.getValue();
    const index = fieldValue.indexOf(possibleMetric);

    if (index < 0) {
      // safety
      return false;
    }

    if (index === 0) {
      // no bracket before possible metric if index is at 0
      return false;
    }

    const charBefore = fieldValue.charAt(index - 1);
    return charBefore === '[';
  }

  private updateReadOnlyState(): void {
    if (this.editor) {
      this.recalculateReadOnly();
      this.editor.updateOptions({ readOnly: this.isReadOnly, extraEditorClassName: this.isReadOnly ? 'lm-readonly-editor' : '' });
    }
  }

  private validateLplQuery(businessClass: string) {
    const model = this.editor.getModel() as monaco.editor.ITextModel;
    monaco.editor.setModelMarkers(model, 'LPL', []);
    this.sohoAlert.removeAllMessages();
    this.model.clearFieldError(this.layoutObject.bindId);
    this.invalidLPLEventDebouncer$.next('false');

    if (!businessClass || businessClass.trim().length <= 0) {
      return;
    }

    if (this.modelField.value) {
      let computeType = this.computeType;
      let dataView = undefined;

      // override, should be more explicit
      if (this.isBusinessMetricCompute) {
        dataView = this.model.getData();
        computeType = 'metric-compute';
      } else if (this.isBusinessMetricTotal) {
        computeType = 'metric-total';
      } else if (this.layoutObject.lplFieldInfo?.mode === 'DerivedField') {
        computeType = 'derived';
      }

      const formResourceId = this.model.formResourceId;
      this.lmBusclassFieldService.makeRequest(businessClass, this.modelField.value, this.isBusClassCompute, this.isBusClassTotal, computeType, dataView, formResourceId)
        .subscribe((result: LMLplValidationResponse) => {
          if (result.validationError) {
            this.sohoAlert.id = 'error-bcf';
            this.model.setError(this.layoutObject.bindId, true);
            this.invalidLPLEventDebouncer$.next('true');
            this.setLPLMarkings(result, model);

            this.sohoAlert.id = this.bindId + '_error';
            this.sohoAlert.addInlineMessage(result.validationError.errorDescription, 'error', false, false, 'error', 'error-bcf-message');
          }
        });
    }
  }

  private addMonacoListeners(): void {
    const model = this.editor.getModel() as monaco.editor.ITextModel;

    model.onDidChangeContent(() => {
      this.modelField.value = model.getValue();
      this.model.evaluateAndApplyDirtyState(this.layoutObject.bindId);
      this.queryValidateEventDebouncer$.next(this.getBusinessClass());
    });

    if (this.isBusinessMetricCompute) {
      // Listen for cursor changes and check to see if it's on a metric.
      // if not then disable the view metric button.
      this.cursorPositionDisposable = this.editor.onDidChangeCursorPosition((e) => {
        const word = this.editor.getModel().getWordAtPosition(e.position);
        this.viewMetricButtonDisabled = !(word && this.wordIsAMetricName(model, word));
      });
    }
  }

  private addMonacoProviders(): void {
    this.registerMonacoHoverProvider();
    this.registerMonacoCompletionItemProvider();
    this.registerDotTriggerModal();
    // this.registerMonacoLinkHandling();


    // this.promptUserAction = this.editor.addAction({
    //   id: 'prompt-user',
    //   label: 'Prompt User',
    //   run: (ed, selectedOption) => {
    //     // Create simple dialog
    //     const dialog = document.createElement('div');
    //     dialog.innerHTML = `
    //       <div style="position:fixed; top:50%; left:50%; transform:translate(-50%,-50%);
    //                   background:white; border:1px solid #ccc; padding:20px; z-index:1000;">
    //         <p>Select or enter number for ${selectedOption}:</p>
    //         <select id="numberSelect">
    //           <option value="">Choose...</option>
    //           <option value="1">1</option>
    //           <option value="5">5</option>
    //           <option value="10">10</option>
    //         </select>
    //         <input type="number" id="numberInput" placeholder="Or enter custom number">
    //         <br><br>
    //         <button onclick="this.parentElement.parentElement.remove()">Cancel</button>
    //         <button id="okBtn">OK</button>
    //       </div>
    //     `;
    //
    //     document.body.appendChild(dialog);
    //
    //     const okBtn = dialog.querySelector('#okBtn') as HTMLButtonElement;
    //     // Store the handler reference
    //     this.okBtnClickHandler = () => {
    //       const selected = (dialog.querySelector('#numberSelect') as HTMLSelectElement).value;
    //       const custom = (dialog.querySelector('#numberInput') as HTMLInputElement).value;
    //       const result = selected || custom;
    //       console.log('Result:', result);
    //
    //       // Remove listener before removing dialog
    //       okBtn.removeEventListener('click', this.okBtnClickHandler);
    //       dialog.remove();
    //     };
    //
    //     okBtn.addEventListener('click', this.okBtnClickHandler);
    //   }
    // });
  }

  /**
   * Not a provider
   * @private
   */
  private registerMonacoLinkHandling(): void {
    if (!this.isBusinessMetricCompute) {
      return;
    }

    const decorationsCollection = this.editor.createDecorationsCollection();

    const mouseMoveDisposable = this.editor.onMouseMove((e) => {
      const position = e.target.position;
      if (!position) {
        decorationsCollection.clear();
        return;
      }

      const model = this.editor.getModel();
      const word = model.getWordAtPosition(position);

      if (word && this.wordIsAMetricName(model, word)) {
        decorationsCollection.set([ {
          range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
          options: {
            inlineClassName: 'lm-metric-link',
            hoverMessage: { value: `Click to open ${word.word}` }
          }
        } ]);
      } else {
        decorationsCollection.clear();
      }
    });

    const mouseLeaveDisposable = this.editor.onMouseLeave(() => {
      decorationsCollection.clear();
    });

    const mouseDownDisposable = this.editor.onMouseDown((e) => {
      const position = e.target.position;
      if (position) {
        const model = this.editor.getModel();
        const word = model.getWordAtPosition(position);
        if (word && this.wordIsAMetricName(model, word)) {
          this.handleMetricLinkClick(word);
        }
      }
    });

    // Store for cleanup
    this.monacoLinkProvider = {
      dispose: () => {
        mouseMoveDisposable.dispose();
        mouseLeaveDisposable.dispose();
        mouseDownDisposable.dispose();
        decorationsCollection.clear();
      }
    };
  }

  private handleMetricLinkClick(word: IWordAtPosition) {
    console.log('Clicked metric:', word.word);
  }

  // private registerMonacoLinkProvider(): void {
  //   if (!this.isBusinessMetricCompute) {
  //     return;
  //   }
  //
  //   // Add CSS to hide links by default and show on hover
  //   const style = document.createElement('style');
  //   style.textContent = `
  //     .monaco-editor .detected-link {
  //       text-decoration: none !important;
  //       border-bottom: none !important;
  //     }
  //     .monaco-editor:hover .detected-link {
  //       text-decoration: underline !important;
  //       border-bottom: 1px solid !important;
  //     }
  //   `;
  //   document.head.appendChild(style);
  //
  //   this.monacoLinkProvider = monaco.languages.registerLinkProvider('LPL', {
  //     provideLinks: (model, token) => {
  //       const links = [];
  //       const text = model.getValue();
  //       const regex = /\[([^\]]+)\]/g;
  //       let match;
  //
  //       while ((match = regex.exec(text)) !== null) {
  //         const startPos = model.getPositionAt(match.index + 1);
  //         const endPos = model.getPositionAt(match.index + match[1].length + 1);
  //
  //         links.push({
  //           range: new monaco.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column),
  //           url: `#${match[1]}`,
  //           tooltip: `Click to open ${match[1]}`
  //         });
  //       }
  //       return { links };
  //     },
  //     resolveLink: (link, token) => {
  //       const metricName = (link.url as string).substring(1); // Remove the #
  //       console.log('Clicked metric:', metricName);
  //       // Your click handler logic here
  //       return link;
  //     }
  //   });
  // }

  // private registerMonacoLinkProvider(): void {
  //   if (!this.isBusinessMetricCompute) {
  //     return;
  //   }
  //
  //   this.monacoLinkProvider = monaco.languages.registerLinkProvider('LPL', {
  //     provideLinks: (model) => {
  //       const links = [];
  //       const text = model.getValue();
  //       const regex = /\[([^\]]+)\]/g;
  //       let match;
  //
  //       while ((match = regex.exec(text)) !== null) {
  //         const startPos = model.getPositionAt(match.index + 1);
  //         const endPos = model.getPositionAt(match.index + match[1].length + 1);
  //
  //         links.push({
  //           range: new monaco.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column),
  //           url: `command:openMetric?${encodeURIComponent(match[1])}`
  //         });
  //       }
  //       return { links };
  //     }
  //   });
  //
  //   // Register the command handler
  //   this.editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyK, (accessor, metricName) => {
  //     // Your click handler logic
  //     console.log('Clicked metric:', metricName);
  //   });
  // }

  private registerMonacoHoverProvider(): void {
    if (!this.isBusinessMetricCompute) {
      return;
    }

    this.monacoHoverProvider = monaco.languages.registerHoverProvider('LPL', {
      provideHover: (model, position) => {
        const word = model.getWordAtPosition(position);
        if (word && this.wordIsAMetricName(model, word)) {
          const formRequestUrlTemplate = urltemplate.parse(this.model.formUrlInfo.formRequestUrl);
          const formRequestUrl = formRequestUrlTemplate.expand({ resourceId: this.model.formResourceId });

          return firstValueFrom(this.lmFormRequestService.getFieldsForBusinessMetric(formRequestUrl, word.word)).then(response => {
            if (response.status === 'COMPLETED') {
              const hoverValue = this.buildHoverValue(response.dataView);
              return {
                range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
                contents: [
                  { value: `**${response.dataView.fields['DisplayName'].value}**` },
                  // { value: `**${word.word}**` },
                  { value: hoverValue, isTrusted: true }
                ]
              };
            } else {
              return {
                range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
                contents: [ { value: response.message } ]
              };
            }

          }).catch((e) => {
            console.log('Error Getting BusinessMetric information', e);
            return {
              range: new monaco.Range(position.lineNumber, word.startColumn, position.lineNumber, word.endColumn),
              contents: [ { value: 'Error getting BusinessMetric information' } ]
            };
          });
        }
        return null;
      }
    });
  }

  private registerDotTriggerModal(): void {
    if (this.isBusinessMetricCompute) {
      this.monacoDotTriggerCompletionProvider = monaco.languages.registerCompletionItemProvider('LPL', {
        triggerCharacters: [ '.' ],

        provideCompletionItems: (model, position) => {
          // Check if the trigger was a dot
          if (this.isGeneralLedgerTotalBusClass) {
          const textBeforeCursor = model.getValueInRange({
            startLineNumber: position.lineNumber,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          });

          if (textBeforeCursor.endsWith('.')) {
            // Show your modal here
            this.onAddMeasure(model, position);

            // Return empty suggestions to prevent default completion
            return { suggestions: [] };
          }
        }

          return { suggestions: [] };
        }
      });
    }
  }

  private registerMonacoCompletionItemProvider(): void {
    if (!this.isBusinessMetricCompute) {
      return;
    }

    this.monacoCompletionProvider = monaco.languages.registerCompletionItemProvider('LPL', {
      triggerCharacters: [ ' ' ],

      provideCompletionItems: (model, position): monaco.languages.ProviderResult<monaco.languages.CompletionList> => {
        return this.processCompletionSuggestions(model, position);
      }
    });
  }

  private processCompletionSuggestions(model: monaco.editor.ITextModel, position: monaco.Position): monaco.languages.ProviderResult<monaco.languages.CompletionList>  {
    // Get current bracket contents
    const bracketContents = this.extractBracketContentsAtPosition(model, position);
    if (!bracketContents?.content) {
      return { suggestions: [] };
    }

    // get metric content minus the brackets
    const currentBracketText = bracketContents.content;
    if (!currentBracketText) {
      return { suggestions: [] };
    }

    // // Check if we're at the end with a space
    // if (!currentBracketText.endsWith(' ')) {
    //   return { suggestions: [] };
    // }

    // Calculate range to replace existing operators
    const lineText = model.getLineContent(position.lineNumber);
    const bracketStart = lineText.indexOf('[' + currentBracketText + ']');
    const spaceIndex = currentBracketText.indexOf(' ');

    if (spaceIndex === -1) {
      return { suggestions: [] };
    }

    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: bracketStart + spaceIndex + 3, // After KPI name and space
      endColumn: bracketStart + currentBracketText.length + 2, // Before closing bracket
    };

    // console.log('Calculated range:', range);
    const suggestions = this.periodOperatorSuggestions(range);
    // console.log('Generated suggestions:', suggestions);

    return { suggestions };
  }

  // private getCurrentBracketText(model: monaco.editor.ITextModel, position: monaco.IPosition): string | null {
  //   const lineText = model.getLineContent(position.lineNumber);
  //   let bracketStart = -1;
  //
  //   for (let i = 0; i < lineText.length; i++) {
  //     if (lineText[i] === '[') {
  //       bracketStart = i;
  //     } else if (lineText[i] === ']' && bracketStart !== -1) {
  //       if (position.column > bracketStart + 1 && position.column <= i + 1) {
  //         return lineText.substring(bracketStart + 1, i);
  //       }
  //       bracketStart = -1;
  //     }
  //   }
  //
  //   return null;
  // }

  private periodOperatorSuggestions(range: monaco.IRange): monaco.languages.CompletionItem[] {
    const periodOperatorList: string[] = [
      'period beginning balance',
      'period ending balance',
      'period ytd beginning balance',
      'period ytd ending balance',
      'prior period',
      'prior year',
      'prior period beginning balance',
      'prior period ending balance',
      'prior period ytd beginning balance',
      'prior period ytd ending balance'
    ];

    const suggestions: monaco.languages.CompletionItem[] =
      periodOperatorList.map((periodOperator: string, index: number) => {
        return {
          label: periodOperator,
          kind: monaco.languages.CompletionItemKind.Value,
          insertText: periodOperator,
          range: range,
          sortText: (index + 1).toString().padStart(2, '0')
        };
      });

      return suggestions;
  }

  public setLPLMarkings(response: LMLplValidationResponse, model: monaco.editor.ITextModel) {
    const markers: monaco.editor.IMarkerData[] = new Array<monaco.editor.IMarkerData>();
    if (response.validationError) {
      const alertType = response.validationError.isWarning ? 'info' : 'error';
      markers.push({
        startLineNumber: response.validationError.row,
        startColumn: response.validationError.startOffset,
        endLineNumber: response.validationError.row,
        endColumn: response.validationError.endOffset,
        message: this.lmLplEditorService.htmlEntityDecode(
          response.validationError.errorDescription
        ),
        severity: alertType === 'error' ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning,
      });
    }
    monaco.editor.setModelMarkers(model, 'LPL', markers);
  }

  private getBusinessClass(): string {
    // lpltext field: BusinessClassCompute, BusinessClassGroup, BusinessClassTotal
    if (!this.layoutObject.lplFieldInfo) {
      return this.getBusinessClassFromDataView('BusinessClass');
    } else {
      // lpl editor condition builder
      if (this.layoutObject.lplFieldInfo.isRelatedField) {
        return this.getBusinessClassFromDataView(this.layoutObject.lplFieldInfo.busClassName);
      } else {
        return this.layoutObject.lplFieldInfo.busClassName;
      }
    }
  }

  private get isGeneralLedgerTotalBusClass(): boolean {
    return this.getBusinessClass() === "GeneralLedgerTotal";
  }

  private getBusinessClassFromDataView(fieldName: string): string {
    const businessClassFld = this.model.getData().fields[fieldName];
    return !businessClassFld ? '' : businessClassFld.value as string;
  }

  private validateBusinessClass(businessClass: string): boolean {
    if (!businessClass) {
      const buttons = [  { text: Soho.Locale.translate('Ok'), click: (e, modal) => { modal.close(true); } }];
      this.lmModalWrapperService
        .error()
        .message(' ')
        .title(Soho.Locale.translate('BusinessClassRequiredMessage'))
        .buttons(buttons)
        .open();

      return false;
    } else {
      return true;
    }
  }

  private setVariables(): void {
    this.isBusClassTotal = this.layoutObject.componentClass === 'BusinessClassTotal';

    this.isBusClassGroup = this.layoutObject.componentClass === 'BusinessClassGroup';

    this.isBusClassCompute = this.layoutObject.componentClass === 'BusinessClassCompute';

    this.isBusinessMetricCompute = this.layoutObject.componentClass === 'BusinessMetric' && this.layoutObject.bindId === 'Compute';

    this.isBusinessMetricTotal = this.layoutObject.componentClass === 'BusinessMetric' && this.layoutObject.bindId === 'TotalCondition';

    this.hideTestMeasures = this.isReadOnly || this.model.isDirty || this.model.isCreateAction || (this.modelField.value as string).length <= 0;

    this.disableDimensions$.next(this.calculateDisableDimensions());
  }

  private calculateDisableDimensions(): boolean {
    if (this.isReadOnly) {
      return true;
    }

    const businessClass = this.getBusinessClass();
    return !businessClass || businessClass.length === 0;
  }

  metricAddMenuItems = (response: AjaxBeforeOpenResponseFunction, options: any) => {

    // Do we need to get the metric to determine if it's a compute or total.
    // I'm thinking we don't at this point.
    // const position = this.editor.getPosition();
    // const model = this.editor.getModel();
    // const word = model.getWordAtPosition(position);
    // if (!word || !this.wordIsAMetricName(model, word)) {
    //   this.lmFieldServiceAdapter.ngZone.run(() => {
    //     this.displayMessage();
    //   });
    //   return;
    // }
    const position = this.editor.getPosition();
    const model = this.editor.getModel();
    const word = model.getWordAtPosition(position);
    const hasMetric =  word ? this.wordIsAMetricName(model, word) : false;

    // if (word && this.wordIsAMetricName(model, word)) {
    //   const metricName = word.word;
    //   this.getMetricDataView(metricName).subscribe({
    //     next: (response: any) => {
    //       console.log('copyMetric', [ metricName, response.dataView ]);
    //       // this.copyMetricFromResponse(metricName, response);
    //     },
    //     error: (e) => {
    //       console.log('Error getting BusinessMetric information', e);
    //       this.lmErrorUtilService.handleUnexpectedError(e);
    //     }
    //   });
    // }
      const metricMenuItemList = [
      { id: LMBusclassFieldComponent.MENUITEM_ADD_METRIC, label: 'Add Metric', disabled: false, visible: true },
      { id: LMBusclassFieldComponent.MENUITEM_ADD_RATIO, label: 'Add Ratio', disabled: false, visible: true },
      { id: LMBusclassFieldComponent.MENUITEM_ADD_VARIANCE_AMOUNT, label: 'Add Variance Amount', disabled: false, visible: true },
      { id: LMBusclassFieldComponent.MENUITEM_ADD_VARIANCE_PERCENT, label: 'Add Variance Percent', disabled: false, visible: true },
      { id: LMBusclassFieldComponent.MENUITEM_ADD_PERIOD_OPERATOR, label: 'Add Period Operator', disabled: !hasMetric, visible: true },
      { id: LMBusclassFieldComponent.MENUITEM_ADD_MEASURE, label: 'Add Measure', disabled: !hasMetric, visible: this.isGeneralLedgerTotalBusClass }
      // { id: LMBusclassFieldComponent.MENUITEM_VIEW_METRIC, label: 'View Metric', disabled: false },
      // { id: LMBusclassFieldComponent.MENUITEM_COPY_METRIC, label: 'Copy Metric', disabled: false }
    ];

    let menuResponse = '';
    metricMenuItemList.forEach(item => {
      if (!item.visible)
        return;

      menuResponse +=
        `<li ${item.disabled ? 'class="disabled"' : ''}>
          <a id=${item.id} href="#">${item.label}</a>
        </li>`;
    });

    response(menuResponse);
  };

  onViewMetricEvent(event: SohoContextMenuEvent) {
    const position = this.editor.getPosition();
    const model = this.editor.getModel();
    const word = model.getWordAtPosition(position);

    // NEED TO GET THE METRIC DATAVIEW HERE TO DETERMINE IF IT'S A TOTAL OR A COMPUTE.
    // Not sure about the previous statement as it's valid to view a compute metric
    // used in a compute metric.
    if (!word || !this.wordIsAMetricName(model, word)) {
      this.lmFieldServiceAdapter.ngZone.run(() => {
        this.displayMessage();
      });
      return;
    }

    if (word && this.wordIsAMetricName(model, word)) {
      const metricName = word.word;
      this.viewMetric(metricName);
    }
  }

  onMetricMenuItemSelected(event: SohoContextMenuEvent) {
    const disallowMeasureOverride = !this.isGeneralLedgerTotalBusClass;

    if (event.args) {
      const menuElement = event.args[0];
      const menuId = menuElement.getAttribute('id');

      switch (menuId) {
        case LMBusclassFieldComponent.MENUITEM_ADD_METRIC:
          this.onAddMetricEvent();
          break;

        case LMBusclassFieldComponent.MENUITEM_ADD_RATIO:
          this.onAddToMetricCompute('ADD_RATIO', disallowMeasureOverride);
          break;

        case LMBusclassFieldComponent.MENUITEM_ADD_VARIANCE_AMOUNT:
          this.onAddToMetricCompute('ADD_VARIANCE_AMOUNT', disallowMeasureOverride);
          break;

        case LMBusclassFieldComponent.MENUITEM_ADD_VARIANCE_PERCENT:
          this.onAddToMetricCompute('ADD_VARIANCE_PERCENT', disallowMeasureOverride);
          break;

        case LMBusclassFieldComponent.MENUITEM_ADD_PERIOD_OPERATOR:
          // is highlighted word a metric name?
          const position = this.editor.getPosition();
          const model = this.editor.getModel();
          const word = model.getWordAtPosition(position);
          // const result = this.getTextBetweenBrackets();
          if (word && this.wordIsAMetricName(model, word)) {
            const result = this.extractBracketContentsAtPosition(model, position);
            const metricName = word.word;
            this.addPeriodOperator(metricName, result);
          }
          break;

        case LMBusclassFieldComponent.MENUITEM_ADD_MEASURE:
          console.log('LMBusclassFieldComponent.MENUITEM_ADD_MEASURE selected');
          const position2 = this.editor.getPosition();
          const model2 = this.editor.getModel();
          this.onAddMeasure(model2, position2);
          break;
      }
      // if (menuId === LMBusclassFieldComponent.MENUITEM_ADD_PERIOD_OPERATOR) {
      //   this.addPeriodOperator(metricName);
      //
      // } else if (menuId === LMBusclassFieldComponent.MENUITEM_VIEW_METRIC) {
      //   this.viewMetric(metricName);
      //
      // } else if (menuId === LMBusclassFieldComponent.MENUITEM_COPY_METRIC) {
      //   this.copyMetric(metricName);
      // }
    }


    // if (event.args && word && this.wordIsAMetricName(model, word)) {
    //   const formRequestUrlTemplate = urltemplate.parse(this.model.formUrlInfo.formRequestUrl);
    //   // const formRequestUrl = formRequestUrlTemplate.expand({ resourceId: this.model.formResourceId });
    //
    //   const metricResource = new KeyedResource({ resourceType: 'form', resourceId: this.model.formResourceId });
    //   metricResource.keyValueArray[1] = word.word;
    //   const metricResourceId = metricResource.generateResourceId(false);
    //
    //   const metricFormUrl = formRequestUrlTemplate.expand({ resourceId: metricResourceId });
    //
    //   return firstValueFrom(this.lmFormRequestService.getFieldsForBusinessMetric(metricFormUrl, word.word)).then(response => {
    //     const menuElement = event.args[0];
    //     const menuId = menuElement.getAttribute('id');
    //
    //     console.log('onMetricMenuItemSelected', [ menuId, word.word, response.dataView ]);
    //
    //     if (menuId === LMBusclassFieldComponent.MENUITEM_ADD_PERIOD_OPERATOR) {
    //       this.addPeriodOperator(word.word, response.dataView);
    //     } else if (menuId === LMBusclassFieldComponent.MENUITEM_VIEW_METRIC) {
    //       this.viewMetric(word.word, response.dataView);
    //     } else if (menuId === LMBusclassFieldComponent.MENUITEM_COPY_METRIC) {
    //       this.copyMetric(word.word, response.dataView);
    //     }
    //
    //   }).catch((e) => {
    //     console.log('Error Getting BusinessMetric information', e);
    //     this.lmErrorUtilService.handleUnexpectedError(e);
    //   });
    // }
  }

  private getTextBetweenBrackets(): string {
    const monacoPosition = this.editor.getPosition();
    const text = this.editor.getModel().getLineContent(monacoPosition.lineNumber);
    // const text = this.editor.getModel().getValue();
    const position = monacoPosition.column;
    const openIndex = text.lastIndexOf('[', position);
    const closeIndex = text.indexOf(']', position);

    if (openIndex === -1 || closeIndex === -1) {
      return '';
    }

    return text.substring(openIndex + 1, closeIndex);
  }

  private extractBracketContentsAtPosition(model: any, position: any): any {
    const lineContent = model.getLineContent(position.lineNumber);
    const regex = /\[([^\]]*)\]/g;
    let match: RegExpExecArray | null;

    while ((match = regex.exec(lineContent)) !== null) {
      const startColumn = match.index + 1; // Monaco uses 1-based columns
      const endColumn = match.index + match[0].length + 1;

      // Check if cursor position is within this bracket
      if (position.column >= startColumn && position.column <= endColumn) {
        return {
          content: match[1],
          range: {
            startLineNumber: position.lineNumber,
            startColumn: startColumn,
            endLineNumber: position.lineNumber,
            endColumn: endColumn
          },
          fullMatch: match[0]
        };
      }
    }

    return '';
  }

  private addPeriodOperator(metricName: string, result: any) {

    const uiFieldSpec: UIFieldSpec = {
      fieldName: metricName,
      parent: '',
      parentFieldTypes: '',
      hasChildren: false,
      businessClass: '',
      fieldType: 'NUMBER',   // type for display and search
      fullLPLName: metricName,
      depth: 1
    };

    const options: LMFieldPickerOperatorOptions = {
      businessClass: '',
      view: 'Measures', // TODO METRIC LMFieldPickerView needs a 'Metrics'
      uiFieldSpec,
      operatorInfo: undefined, // LMOperatorInfo,
      auditData: false
    };

    this.lmFieldPickerService.openFieldOperatorDialog(options).subscribe({
      next: (response: LMFieldPickerSelectResponse) => {
        if (!response) {
          return;
        }
        this.editor.executeEdits('replace', [
          {
            range: result.range,
            text: `[${response.fieldName}]`
          }
        ]);
      },
      error: (error: any) => {
        const error2: LMError = { message: error.message, status: error.status, details: error.stack };
        this.lmErrorUtilService.handleUnexpectedError(error2);
      }
    });
  }

  private viewMetric(metricName: string) {
    const metricUrl = this.getMetricOpenUrl(metricName);

    if (this.lmFieldServiceAdapter.lmContextService.useListFormOverlay()) {
      if (this.lmOverlayService.isOpening) {
        return;
      }
      this.lmOverlayService.isOpening = true;

      const overLayInfo: LMOverlayInfo = {
        open: true,
        url: metricUrl,
        navigationType: 'form',
        listOpenedId: '',
        resourceId: '',
        isNavigation: false
      };

      if (this.model.isInOverlay) {
        overLayInfo.replaceCurrentContent = true;
        overLayInfo.insideOverlayNav = true;
      } else if (this.lmOverlayService.hasOpenOverlay) {
        overLayInfo.replaceCurrentContent = true;
      }

      this.lmOverlayService.open(overLayInfo);
    } else {
      this.lmFieldServiceAdapter.lmGlobalUtilService.navigateByUrl(metricUrl);
    }
  }

  private copyMetric(metricName: string) {
    this.getMetricDataView(metricName).subscribe({
      next: (response: any) => {
        console.log('copyMetric', [ metricName, response.dataView ]);
        // this.copyMetricFromResponse(metricName, response);
      },
      error: (e) => {
        console.log('Error Getting BusinessMetric information', e);
        this.lmErrorUtilService.handleUnexpectedError(e);
      }
    });


    // const formRequestUrlTemplate = urltemplate.parse(this.model.formUrlInfo.formRequestUrl);
    // const formRequestUrl = formRequestUrlTemplate.expand({ resourceId: this.model.formResourceId });
    //
    // this.lmFormRequestService.getFieldsForBusinessMetric(formRequestUrl, metricName).subscribe({
    //   next: (response: any) => {
    //     console.log('copyMetric', [ metricName, response.dataView ]);
    //     // this.copyMetricFromResponse(metricName, response);
    //   },
    //   error: (e) => {
    //     console.log('Error Getting BusinessMetric information', e);
    //     this.lmErrorUtilService.handleUnexpectedError(e);
    //   }
    // });

    // const metricText = this.lmLplEditorService.getMetricText(metricName);
    // this.lmLplEditorService.copyMetric(metricText, dataView);
  }

  private getMetricOpenUrl(metricName: string): string {
    const metricResource = new KeyedResource({ resourceType: 'form', resourceId: this.model.formResourceId });
    metricResource.keyValueArray[1] = metricName;
    const metricResourceId = metricResource.generateResourceId(false);

    const formRequestUrlTemplate = urltemplate.parse(this.model.formUrlInfo.formRequestUrl);
    return formRequestUrlTemplate.expand({ resourceId: metricResourceId });
  }

  private getMetricDataView(metricName: string): Observable<any> {
    const formRequestUrlTemplate = urltemplate.parse(this.model.formUrlInfo.formRequestUrl);
    const formRequestUrl = formRequestUrlTemplate.expand({ resourceId: this.model.formResourceId });
    return this.lmFormRequestService.getFieldsForBusinessMetric(formRequestUrl, metricName);
  }

  public displayMessage() {

    const dialogId = 'metric-message';
    const buttons = [
      {
        id: /*dialogId + '_' + */LMDialogButtonKey.CLOSE,
        text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
        click: (e, modal) => {
          modal.close(true);
        }
      },
    ];

    const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-metric-message-modal' } ];
    const messageDialog = this.lmModalWrapperService
      .message()
      .attributes(attributes)
      // .title(Soho.Locale.translate('MetricNotHighlighted'))
      // .message(Soho.Locale.translate('MetricNotHighlightedMessage'))
      .title('Metric Not Highlighted')
      .message('Please place your cursor on a Metric to view it.')
      .buttons(buttons)
      .open();
  }

  // private addCustomTooltipListener() {
  //   if (this.isBusinessMetricCompute) {
  //     this.mouseMoveListener = (e: MouseEvent) => {
  //       const position = this.editor.getTargetAtClientPoint(e.clientX, e.clientY)?.position;
  //       if (position) {
  //         const word = this.editor.getModel()?.getWordAtPosition(position);
  //         if (word && this.wordIsAMetricName(this.editor.getModel(), word)) {
  //           this.showTooltip(e, word.word);
  //         } else {
  //           this.hideTooltip();
  //         }
  //       }
  //     };
  //
  //     this.mouseLeaveListener = () => {
  //       this.hideTooltip();
  //     };
  //
  //     this.editor.getDomNode()?.addEventListener('mousemove', this.mouseMoveListener);
  //     this.editor.getDomNode()?.addEventListener('mouseleave', this.mouseLeaveListener);
  //   }
  // }
  //
  // private showTooltip(mouseEvent: MouseEvent, metricName: string): void {
  //   this.hideTooltip();
  //
  //   const container = document.createElement('div');
  //   container.style.cssText = 'position: absolute; background: #333; color: white; padding: 8px; border-radius: 3px; font-size: 12px; max-width: 300px; z-index: 99999; box-shadow: 0 2px 8px rgba(0,0,0,0.3); pointer-events: none;';
  //
  //   const title = document.createElement('div');
  //   title.style.cssText = 'font-weight: bold; margin-bottom: 8px;';
  //   title.textContent = metricName;
  //
  //   const table = document.createElement('table');
  //   table.style.cssText = 'border-collapse: collapse; width: 100%;';
  //
  //   const rows = [
  //     [ 'Property', 'Value' ],
  //     [ 'Type', 'Business Metric' ],
  //     [ 'Status', 'Active' ],
  //     [ 'Category', 'KPI' ]
  //   ];
  //
  //   rows.forEach((rowData, index) => {
  //     const row = document.createElement('tr');
  //     rowData.forEach(cellData => {
  //       const cell = document.createElement(index === 0 ? 'th' : 'td');
  //       cell.style.cssText = 'text-align: left; padding: 4px; border: 1px solid #555;';
  //       cell.textContent = cellData;
  //       row.appendChild(cell);
  //     });
  //     table.appendChild(row);
  //   });
  //
  //   container.appendChild(title);
  //   container.appendChild(table);
  //
  //   // Position tooltip above cursor
  //   container.style.left = mouseEvent.clientX + 'px';
  //   container.style.top = (mouseEvent.clientY - 120) + 'px';
  //
  //   document.body.appendChild(container);
  //   this.tooltipElement = container;
  // }
  //
  // private hideTooltip(): void {
  //   if (this.tooltipElement) {
  //     document.body.removeChild(this.tooltipElement);
  //     this.tooltipElement = null;
  //   }
  // }


  // private buildFieldLookupUrl() {
  //   let urlParams: LMUrlParams = { ...this.lmFieldServiceAdapter.lmContextService.getContext().contextSessionKeys };
  //   let url =  + this.model.formUrlInfo.basePath + '/list/{resourceId}';
  //   let delimiter = '?';
  //
  //   this.lmFieldServiceAdapter.lmUrlUtilService.buildUrl2
  //   // todo needs context information. perhaps
  //   String cskString = UrlUtils.buildContextSessionKeysParamString(info.codec, info.contextSessionKeyMap);
  //   if (cskString.trim().length() > 0)
  //   {
  //     url += delimiter + cskString;
  //     delimiter = '&';
  //   }
  //
  //
  //   return LMUrlUtilService.buildUrl2(
  //     urlInfo.basePath,
  //     urlInfo.resource.resourceType,
  //     urlInfo.resource.generateResourceId(true),
  //     urlParamsToBeEncoded,
  //     urlParams);
  //
  //   return url;
  // }


}

class LMMetricInfo {
  metricName: string;
  measure?: string;
  periodOperators?: string;

  constructor(metricText: string) {
    const match = metricText.match(/^([^.\s]+)(?:\.([^\s]+))?(?:\s+(.+))?$/);
    if (match) {
      this.metricName = match[1];
      this.measure = match[2];
      this.periodOperators = match[3];
    } else {
      const index = metricText.indexOf('.');
      if (index > -1) {
        metricText = metricText.substring(0, index);
      }
      this.metricName = metricText;
    }
  }

  setMeasure(measure: string): void {
    this.measure = measure;
  }

  setPeriodOperators(periodOperators: string): void {
    this.periodOperators = periodOperators;
  }

  toString(): string {
    let result = this.metricName;

    if (this.measure) {
      result += '.' + this.measure;
    }

    if (this.periodOperators) {
      result += ' ' + this.periodOperators;
    }

    return result;
  }
}


